import { useEffect, useState } from 'react'
import { useTheme } from '@/admin-design/_common/theme/ThemeProvider'

/** The chart colours as `rgb(r g b)` strings, read from the tokens.
 *
 *  SVG presentation attributes (recharts' `stroke`, `fill`) do not take
 *  `rgb(var(--chart-1))`, so the values are read from the document's computed
 *  style instead — tokens.css stays the one place a colour is written — and
 *  read again when the theme changes. */
export interface ChartColors {
  revenue: string
  spend: string
  profit: string
  grid: string
  axis: string
  card: string
  series: string[]
}

const FALLBACK = 'currentColor'

function rgb(style: CSSStyleDeclaration | null, name: string): string {
  const v = style?.getPropertyValue(`--${name}`).trim()
  // A token that points at another (`--chart-grid: var(--border)`) comes back
  // as the reference in some engines; follow it once.
  const ref = v && /^var\(--([\w-]+)\)$/.exec(v)
  if (ref) return rgb(style, ref[1])
  return v ? `rgb(${v})` : FALLBACK
}

export function readChartColors(root: Element | null = typeof document === 'undefined' ? null : document.documentElement): ChartColors {
  const style = root && typeof getComputedStyle === 'function' ? getComputedStyle(root) : null
  return {
    revenue: rgb(style, 'chart-1'),
    spend: rgb(style, 'chart-2'),
    profit: rgb(style, 'foreground'),
    grid: rgb(style, 'chart-grid'),
    axis: rgb(style, 'muted-foreground'),
    card: rgb(style, 'card'),
    series: [1, 2, 3, 4, 5].map((n) => rgb(style, `chart-${n}`)),
  }
}

export function useChartColors(): ChartColors {
  const { resolvedMode } = useTheme()
  const [colors, setColors] = useState(readChartColors)
  useEffect(() => {
    // The provider sets data-mode in the same commit; read after it lands.
    const id = requestAnimationFrame(() => setColors(readChartColors()))
    return () => cancelAnimationFrame(id)
  }, [resolvedMode])
  return colors
}
